// styles.js
export const tableStyles = {
    // Table container and paper
    tablePaper: {
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        position: "relative" // For absolute positioning of loading overlay
    },

    table: {
        border: "none"
    },

    // Header styles with sticky positioning
    tableHead: {
        "& tr": {
            backgroundColor: "#f8f9fa"
        },
        // Make header sticky
        position: "sticky",
        top: 0,
        zIndex: 2,
        "& th": {
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "#f8f9fa" // Keep background color when sticky
        }
    },

    tableHeadCell: {
        fontSize: "0.875rem",
        fontWeight: "600",
        color: "#333",
        borderBottom: "2px solid #e0e0e0",
        padding: "12px 16px",
        backgroundColor: "#f8f9fa",
        "& .Mui-TableHeadCell-Content": {
            justifyContent: "space-between"
        },
        "& .MuiTableSortLabel-root": {
            color: "inherit !important"
        }
    },

    // Body styles
    tableBodyCell: {
        fontSize: "0.875rem",
        padding: "12px 16px",
        borderBottom: "1px solid #f0f0f0",
        "&:hover": {
            backgroundColor: "#fafafa"
        }
    },

    tableBodyRow: {
        "&:last-child td": {
            borderBottom: "none"
        }
    },

    // Search field with primary icon
    searchTextField: {
        minWidth: "300px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "6px",
            "& fieldset": {
                borderColor: "#e0e0e0"
            },
            "&:hover fieldset": {
                borderColor: "#bdbdbd"
            },
            "&.Mui-focused fieldset": {
                borderColor: "primary.main"
            }
        },
        "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            padding: "8px 14px"
        },
        "& .MuiSvgIcon-root": {
            color: "primary.main" // Search icon primary color
        }
    },

    // Pagination with primary icons
    pagination: {
        "& .MuiTablePagination-selectLabel": {
            fontSize: "0.875rem",
            color: "#666"
        },
        "& .MuiTablePagination-displayedRows": {
            fontSize: "0.875rem",
            color: "#666"
        },
        "& .MuiSelect-select": {
            fontSize: "0.875rem",
            padding: "4px 8px"
        },
        "& .MuiSvgIcon-root": {
            color: "primary.main" // Pagination arrows primary color
        },
        "& .MuiIconButton-root": {
            color: "primary.main", // Pagination circle buttons
            "&.Mui-disabled": {
                color: "action.disabled"
            },
            "&:hover": {
                backgroundColor: "action.hover"
            }
        }
    },

    // Selection checkboxes primary color
    selectionCheckbox: {
        color: "primary.main",
        "&.Mui-checked": {
            color: "primary.main"
        },
        "&.Mui-disabled": {
            color: "action.disabled"
        }
    },

    // Toolbar buttons with primary icons
    toolbarButtons: {
        color: "primary.main",
        "& .MuiSvgIcon-root": {
            color: "inherit" // Inherit from button
        },
        "&:hover": {
            backgroundColor: "action.hover",
            color: "primary.dark"
        }
    },

    // Toolbars
    // Enhanced toolbar container - responsive
    toolbarContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
        backgroundColor: "transparent",
        flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
        gap: { xs: 2, sm: 0 }
    },

    // Enhanced button group - responsive
    buttonGroup: {
        display: "flex",
        gap: "4px",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "flex-end"
    },

    // Enhanced bottom section - responsive
    bottomSection: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 16px",
        gap: 2,
        flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
        textAlign: { xs: "center", sm: "left" }
    },

    // Enhanced selection count - responsive
    selectionCount: {
        minWidth: "120px",
        flexShrink: 0,
        textAlign: { xs: "center", sm: "left" }
    },

    // Enhanced row info - responsive
    rowInfo: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        order: { xs: 3, sm: 2 } // Reorder for mobile
    },

    // Enhanced pagination wrapper - responsive
    paginationWrapper: {
        minWidth: "auto",
        flexShrink: 0,
        order: { xs: 2, sm: 3 } // Reorder for mobile
    },

    // Enhanced skeleton props
    skeleton: {
        borderRadius: 1,
        animation: "wave"
    },

    topToolbar: {
        backgroundColor: "transparent",
        minHeight: "60px",
        padding: "16px",
        gap: "16px"
    },

    bottomToolbar: {
        backgroundColor: "transparent",
        minHeight: "52px",
        padding: "8px 16px"
    },

    // Custom layout
    toolbarContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
        backgroundColor: "transparent"
    },

    buttonGroup: {
        display: "flex",
        gap: "4px",
        flexWrap: "nowrap",
        alignItems: "center"
    },

    paginationContainer: {
        display: "flex",
        justifyContent: "flex-end",
        padding: "8px 16px"
    },

    // Active-filter chips bar (below toolbar, above table)
    activeFiltersBar: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        padding: "8px 16px",
        backgroundColor: "#fbfbfb",
        borderBottom: "1px solid #e0e0e0"
    },

    // Fixed table height container with scroll
    tableContainer: {
        "& .MuiTableContainer-root": {
            maxHeight: "60vh",
            minHeight: "60vh",
            overflow: "auto",
            position: "relative",
            // Firefox
            scrollbarWidth: "thin",
            scrollbarColor: "#bdbdbd #f1f1f1",
            // Webkit — always-visible, prominent scrollbar
            "&::-webkit-scrollbar": {
                height: "10px",
                width: "10px"
            },
            "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "4px"
            },
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bdbdbd",
                borderRadius: "4px",
                border: "2px solid #f1f1f1",
                "&:hover": {
                    backgroundColor: "#888"
                }
            },
            "&::-webkit-scrollbar-corner": {
                backgroundColor: "#f1f1f1"
            }
        },
        "& .MuiTable-root": {
            position: "relative"
        }
    },

    // Loading and empty states - CENTERED
    loadingContainer: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px",
        color: "text.secondary",
        zIndex: 4
    },

    emptyContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px",
        color: "text.primary",
        zIndex: 3
    },

    loadingText: {
        marginTop: "16px",
        fontSize: "0.875rem",
        fontWeight: "medium"
    },

    emptyText: {
        marginTop: "16px",
        fontSize: "0.875rem",
        fontWeight: "medium"
    },

    // Loading overlay
    loadingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4
    },

    // Bottom info section
    bottomSection: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 16px",
        gap: 2
    },

    selectionCount: {
        minWidth: "120px",
        flexShrink: 0
    },

    rowInfo: {
        flex: 1,
        display: "flex",
        justifyContent: "right"
    },

    rowInfoText: {
        fontWeight: "medium"
    },

    paginationWrapper: {
        minWidth: "auto",
        flexShrink: 0
    }
};

export const rowStatusStyles = {
    // Bold row style when assetActionStatus is present
    boldRow: {
        fontWeight: "bold",
        backgroundColor: "#fff3cd", // Light yellow background for emphasis
        "&:hover": {
            backgroundColor: "#ffeaa7" // Darker yellow on hover
        }
    },

    // Normal row style when assetActionStatus is not present
    normalRow: {
        fontWeight: "normal",
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: "#fafafa"
        }
    }
};

// Default messages
export const defaultMessages = {
    loading: "Loading...",
    noRecords: "No records to display"
};

export default tableStyles;
